/**
 * Boundary between the notification read API and the shapes the center renders.
 */
import { NOTIFICATION_SEVERITY, type Notification, type NotificationItem } from 'types/modules/notification-center/notification'

import type {
  ListNotificationItemsApi,
  ListNotificationPreferencesApi,
  ListNotificationsApi,
  NotificationApi,
  NotificationItemApi,
} from 'types/api/notification-center/notification'
import type {
  NotificationItemListResponse,
  NotificationListResponse,
  NotificationPayload,
  NotificationPreference,
  NotificationSeverity,
} from 'types/modules/notification-center/notification'

const SEVERITIES: readonly string[] = Object.values(NOTIFICATION_SEVERITY)

const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/

/**
 * The response records timestamps as `LocalDateTime` although the columns are
 * `timestamptz`, so the offset is dropped in serialization and a bare timestamp
 * would be read as browser-local. Services run UTC; drop this once the BE sends
 * an offset.
 */
const asUtc = (value: string): string => (ZONED.test(value) ? value : `${value}Z`)

const asText = (value: unknown): string | undefined => (typeof value === 'string' && value.length > 0 ? value : undefined)

const asSeverity = (value: unknown): NotificationSeverity | undefined => {
  const severity = asText(value)
  return severity && SEVERITIES.includes(severity) ? (severity as NotificationSeverity) : undefined
}

const asScope = (value: unknown) => (value === 'MASTER' || value === 'DAILY' ? value : undefined)

const asResolution = (value: unknown) => (value === 'APPLIED' || value === 'REJECTED' ? value : undefined)

const fromPayload = (payload: unknown): NotificationPayload | undefined => {
  if (typeof payload !== 'object' || payload === null) return undefined
  const fields = payload as Record<string, unknown>
  return {
    details: asText(fields.details),
    severity: asSeverity(fields.severity),
    jobId: asText(fields.jobId),
    routeId: asText(fields.routeId),
    serviceDate: asText(fields.serviceDate),
    scope: asScope(fields.scope),
    resolution: asResolution(fields.resolution),
  }
}

export const fromNotificationApi = (dto: NotificationApi): Notification => ({
  id: dto.id,
  category: dto.category,
  source: dto.source,
  status: dto.status,
  title: dto.title,
  body: dto.body ?? undefined,
  entityType: dto.entityType ?? undefined,
  entityId: dto.entityId ?? undefined,
  entityLabel: dto.entityLabel ?? undefined,
  totalCount: dto.totalCount ?? undefined,
  succeededCount: dto.succeededCount ?? undefined,
  failedCount: dto.failedCount ?? undefined,
  hasItems: dto.hasItems ?? false,
  payload: fromPayload(dto.payload),
  readAt: dto.readAt ? asUtc(dto.readAt) : undefined,
  occurredAt: asUtc(dto.occurredAt),
  lastActivityAt: asUtc(dto.lastActivityAt),
})

export const fromListNotificationsApi = (dto: ListNotificationsApi): NotificationListResponse => ({
  notifications: dto.notifications.map(fromNotificationApi),
  totalItems: dto.totalItems,
  totalPages: dto.totalPages,
})

export const fromNotificationItemApi = (dto: NotificationItemApi): NotificationItem => ({
  refType: dto.refType ?? undefined,
  refId: dto.refId ?? undefined,
  refLabel: dto.refLabel ?? undefined,
  outcome: dto.outcome,
  reasonCode: dto.reasonCode ?? undefined,
  reasonMessage: dto.reasonMessage ?? undefined,
})

export const fromListNotificationItemsApi = (dto: ListNotificationItemsApi): NotificationItemListResponse => ({
  items: dto.items.map(fromNotificationItemApi),
  totalItems: dto.totalItems,
  totalPages: dto.totalPages,
  truncated: dto.truncated,
})

export const fromListNotificationPreferencesApi = (dto: ListNotificationPreferencesApi): NotificationPreference[] => dto.preferences
